/*
 * MANAGE FENCERS (direction §2, contract §§2-3).
 *
 * "Entry from Settings and the list header, not a nav tab; neutral wording for all
 * personas ('Tracked fencers', 'Add', 'N of 10'). Roster rows: name + club, red
 * 'Remove' with undo (no confirmation — re-adding is cheap; the row visibly
 * leaves). Add mode: one search over fencers and clubs ... Commit bar does the cap
 * math aloud ('3 selected · 8 of 10 after adding'); the filled red button is the
 * primary-action tier."
 *
 * NEUTRAL WORDING IS THE ONE COPY RULE HERE, and it is a real constraint rather
 * than a preference. The same screen serves a fencer tracking themselves, a parent
 * tracking two children and a coach tracking ten students. "My fencers" is wrong
 * for the coach, "My students" is wrong for the parent, and "Athletes" is wrong for
 * everyone. So: the bare noun, every time, including in the empty state.
 *
 * The word is now "Fencers" rather than "Tracked fencers" (decision 325) —
 * "tracked" read as surveillance — which changes nothing about the rule above. It
 * is still the possessive and the role noun that are barred, and the replacement
 * had to clear the same bar: "My fencers" was proposed and rejected on exactly
 * this paragraph. `manage.test.tsx` is what enforces it.
 *
 * NO CONFIRMATION ON REMOVE, and the undo is what makes that safe. §2 gives the
 * reasoning: re-adding is cheap, and the row visibly leaving is itself the
 * feedback. A confirm dialog on an action that costs one tap to reverse trains
 * people to dismiss dialogs, which is expensive on the day one of them matters.
 * The undo is offered for the LAST removal only — a stack of undos would be a
 * history feature, and the roster is small enough to re-add from search.
 *
 * WHY ADD IS A COMMIT RATHER THAN A LIVE TOGGLE. The search lets you pick several
 * people, and the cap arithmetic is only meaningful over a batch: "3 selected · 8
 * of 10 after adding" cannot be said about a toggle that already fired. So picks
 * accumulate in the search component and one button applies them.
 *
 * THE CAP IS STATED THREE TIMES, deliberately: on the roster header ("N of 10"), in
 * the search's own count, and in the commit bar's arithmetic. That is not
 * redundancy — it is the same number answering three different questions ("where am
 * I", "what have I picked", "what happens if I press this").
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { FencerSearch } from "../components/FencerSearch";
import { PreferenceTree, type SetPreference } from "../components/PreferenceTree";
import {
  api,
  isConflict,
  list,
  type AlertPreferences,
  type FencerResult,
  type RosterMember,
  type User,
} from "../lib/api";
import { useMultiList, type ListRow } from "../lib/useMultiList";
import styles from "./ManageFencers.module.css";

export interface ManageFencersProps {
  user: User;

  /**
   * The account's feature grants from /api/me. Passed down rather than fetched
   * here, because the shell already holds them and a second read would be a
   * second answer to the same question.
   */
  features: string[];

  /**
   * Reported when the account changes underneath this screen, which happens
   * twice here: switching which list is being edited writes
   * `users.current_list_id`, and deleting the current list makes the server
   * re-point that column at the primary. The shell holds the user, and a stale
   * copy of it would leave this screen reading a list that no longer exists.
   */
  onUserChanged: (user: User) => void;

  /**
   * How many lists the account may hold, from /api/me (decision 889). At it,
   * "New list" is not offered (Kevin): its only outcome would be the server's
   * refusal. Absent when the server could not read it, and then the button
   * stays and the 409 is the limit.
   */
  listCountCap?: number;
}

/*
 * THE ROSTER SIZE IS NO LONGER REPORTED UPWARDS (decision 501). This screen used
 * to hand the count to the session so the grouped views could collapse their
 * fencer axis at one (359) without a reload. Each view now counts its own list
 * scope and sends the number with its own payload, which it re-fetches on the
 * way in — so there was nothing left for the callback to write to, and a prop
 * that writes nowhere is worse than no prop.
 */

/**
 * The picks that would change the roster: everything chosen except fencers
 * already on it. The commit sends only these, and the cap arithmetic counts
 * only these, because adding a member is a no-op.
 */
function adding(chosen: FencerResult[], members: RosterMember[] | null): FencerResult[] {
  const on = new Set((members ?? []).map((m) => m.fencer_id));
  return chosen.filter((f) => !on.has(f.fencer_id));
}

/** The fencer whose removal can still be undone, kept whole so re-adding needs no fetch. */
interface Removed {
  fencerID: string;
  name: string;
}

/**
 * THE SCREEN'S BODY IS ONE OF THREE THINGS, and the two extra ones are STEPS
 * rather than screens (plan §6.1).
 *
 *   roster        — what this screen has always been.
 *   create        — name the new list.
 *   notifications — what should it send? Asked at the moment it is saved
 *                   (plan rule 6), because that is when somebody knows the
 *                   answer and is thinking about it.
 *
 * INLINE, ON ONBOARDING'S PRECEDENT: the body is replaced while a step is
 * active. Not a modal, which would need a focus trap for a two-question flow,
 * and not a route, which would put a half-made list in the browser's history
 * and give the back button a meaning nobody chose.
 */
type Step = "roster" | "create" | "notifications";

export function ManageFencers({ user, features, onUserChanged, listCountCap }: ManageFencersProps) {
  const { t } = useTranslation();

  const listID = user.current_list_id;

  /*
   * TWO CONDITIONS, CHECKED SEPARATELY (Kevin's rule, 2026-09-14). `granted`
   * decides whether the list section exists at all; `lists.length` decides
   * whether the PICKER inside it does. An account holding the flag with one
   * list still gets New list, Rename and Delete -- there is just nothing to
   * pick between.
   */
  const { granted, lists, refresh } = useMultiList(features);

  const [step, setStep] = useState<Step>("roster");
  const [created, setCreated] = useState<ListRow | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [listBusy, setListBusy] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [members, setMembers] = useState<RosterMember[] | null>(null);
  // The member cap, from the roster response alongside the members (decision
  // 889): runtime configuration on the server, so there is no number to start
  // from here, and nothing renders until both have arrived.
  const [cap, setCap] = useState<number | null>(null);
  const [chosen, setChosen] = useState<FencerResult[]>([]);
  const [removed, setRemoved] = useState<Removed | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!listID) return;
    let cancelled = false;

    api
      .roster(listID)
      .then((body) => {
        if (cancelled) return;
        setMembers(list(body.members));
        setCap(body.cap);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [listID]);

  /*
   * WHO IS ON THE ACCOUNT'S OTHER LISTS, so the search can say "On list 'X'"
   * for every list a fencer is already on and not only the one being edited
   * (Kevin, post-weekend item 3): adding someone to a second list is often a
   * mistake about which list is open. One roster read per other list, through
   * the endpoint that already exists; an account has few lists, and a list
   * whose read fails simply says nothing rather than failing the screen.
   *
   * Keyed on the list IDS as a string so a refresh that returns the same lists
   * does not re-read them.
   */
  const otherListKey = lists
    .filter((l) => l.id !== listID)
    .map((l) => l.id)
    .join(",");
  const [otherRosters, setOtherRosters] = useState<Map<string, Set<string>>>(new Map());
  useEffect(() => {
    const ids = otherListKey ? otherListKey.split(",") : [];
    let cancelled = false;
    void Promise.all(
      ids.map((id) =>
        api
          .roster(id)
          .then((b) => [id, new Set(list(b.members).map((m) => m.fencer_id))] as const)
          .catch(() => null),
      ),
    ).then((rows) => {
      if (cancelled) return;
      setOtherRosters(new Map(rows.filter((r) => r !== null)));
    });
    return () => {
      cancelled = true;
    };
  }, [otherListKey]);

  /*
   * ALL THREE MUTATIONS APPLY THE SERVER'S ANSWER rather than patching local state.
   * Every roster endpoint returns the whole roster for exactly this reason: the cap
   * is enforced server-side, so a client that predicted the outcome would be wrong
   * in precisely the case that matters — the eleventh add.
   */
  function apply(body: { members: RosterMember[] | null; cap: number }) {
    setMembers(list(body.members));
    setCap(body.cap);
  }

  async function commitAdds() {
    if (!listID || chosen.length === 0) return;
    setBusy(true);
    setError(null);

    try {
      // Sequential, not Promise.all: the cap is checked inside each insert's
      // transaction, so three parallel requests against two free slots would have
      // two of them race and the third fail with no way to say which. One at a time
      // means the refusal names the fencer it refused.
      //
      // A pick already on the list is skipped rather than sent: the search says
      // "On {list}" for it and keeps it selectable, and adding it would be a
      // no-op request.
      for (const f of adding(chosen, members)) {
        apply(await api.addRosterMember(listID, f.fencer_id));
      }
      setChosen([]);
    } catch (err) {
      // `isConflict` AND NOT A STATUS COMPARISON, which is a correction: this
      // read `kind === "server" && status === 409`, and the client's failure
      // vocabulary turns every 409 into `onboarding-incomplete` with no status
      // attached — so the branch could not fire and the eleventh fencer was
      // reported as a generic save failure rather than as the cap.
      setError(isConflict(err) ? t("manage.errorCap", { cap }) : t("error.couldNotSave"));
      // The roster is re-read even on failure: some of the batch may have landed,
      // and leaving the screen showing a pre-batch roster after a partial success
      // is the one outcome the user cannot reason about.
      if (listID) {
        try {
          apply(await api.roster(listID));
        } catch {
          // Already reporting a failure; a second one adds nothing.
        }
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove(member: RosterMember) {
    if (!listID) return;
    setError(null);
    // Offered BEFORE the request, so the undo is on screen the moment the row
    // leaves rather than one round trip later.
    setRemoved({ fencerID: member.fencer_id, name: member.name });
    try {
      apply(await api.removeRosterMember(listID, member.fencer_id));
    } catch {
      setRemoved(null);
      setError(t("error.couldNotSave"));
    }
  }

  async function undoRemove() {
    if (!listID || !removed) return;
    const target = removed;
    setRemoved(null);
    try {
      apply(await api.addRosterMember(listID, target.fencerID));
    } catch {
      // Re-offer the undo rather than swallowing it: the fencer is still off the
      // list, so the action is still available and still the right one.
      setRemoved(target);
      setError(t("error.couldNotSave"));
    }
  }

  // --- the list verbs ---------------------------------------------------------

  const openList = lists.find((l) => l.id === listID) ?? null;
  const primary = lists.find((l) => l.is_primary) ?? null;

  /** Switch which list this screen edits (decision 501's narrowed meaning). */
  async function switchList(id: string) {
    setListError(null);
    setRenaming(false);
    setConfirmingDelete(false);
    try {
      const { user: updated } = await api.currentList(id);
      onUserChanged(updated);
    } catch {
      setListError(t("error.couldNotSave"));
    }
  }

  /**
   * ARRIVING AT THIS SCREEN OPENS THE PRIMARY LIST, whatever list was last
   * edited (decision 527; Kevin: "When clicking Settings -> 'Manage', default
   * to primary list"). ONCE PER MOUNT, via the ref: without it, switching
   * lists inside a visit would be reverted on the very next render, and the
   * create flow's new list would be closed the moment it opened. Gated on
   * `granted` so an ungranted account -- which has exactly one list, already
   * primary -- issues no write at all (decision 509's regression property).
   */
  const defaulted = useRef(false);
  useEffect(() => {
    if (defaulted.current || !granted) return;
    const p = lists.find((l) => l.is_primary);
    if (!p) return; // the lists have not arrived yet
    defaulted.current = true; // once per arrival, so an in-visit switch sticks
    if (p.id !== listID) void switchList(p.id);
  }, [granted, lists, listID]);

  /**
   * Create, then make it the list being edited, then ask what it should send.
   *
   * THE CURRENT-LIST WRITE IS THE CLIENT'S (plan §6.1): a coach who just made a
   * list wants to put fencers in it, and doing it here rather than server-side
   * keeps `SetCurrentList` the only writer of that column on the create path.
   *
   * THE 409 IS TWO REFUSALS AND THE CLIENT PICKS THE SENTENCE. Both the taken
   * name and the list cap answer 409, and the client writes every sentence
   * (decision 416), so it has to know which. It re-reads the lists first and
   * asks whether one now holds the typed name -- against server truth fetched
   * after the refusal, so a list made on another device a moment ago gets the
   * right sentence rather than the plausible one.
   */
  async function createList(name: string, description: string) {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    if (!trimmedName) return;

    setListBusy(true);
    setListError(null);
    try {
      const row = await api.createList({
        name: trimmedName,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      });
      await refresh();
      const { user: updated } = await api.currentList(row.id);
      onUserChanged(updated);
      setCreated(row);
      setStep("notifications");
    } catch (err) {
      if (isConflict(err)) {
        const rows = await refresh().catch(() => lists);
        setListError(
          rows.some((l) => l.name === trimmedName)
            ? t("manage.lists.errorDuplicate")
            : t("manage.lists.errorCap"),
        );
      } else {
        setListError(t("error.couldNotSave"));
      }
    } finally {
      setListBusy(false);
    }
  }

  async function renameList(name: string) {
    const trimmed = name.trim();
    if (!listID || !trimmed) return;
    setListBusy(true);
    setListError(null);
    try {
      // Name only: an absent field is unchanged server-side, so this cannot
      // blank the description.
      await api.updateList(listID, { name: trimmed });
      await refresh();
      setRenaming(false);
    } catch (err) {
      setListError(
        isConflict(err) ? t("manage.lists.errorDuplicate") : t("error.couldNotSave"),
      );
    } finally {
      setListBusy(false);
    }
  }

  async function makePrimary() {
    if (!listID) return;
    setListBusy(true);
    setListError(null);
    try {
      await api.setPrimaryList(listID);
      await refresh();
    } catch {
      setListError(t("error.couldNotSave"));
    } finally {
      setListBusy(false);
    }
  }

  /**
   * Delete the open list.
   *
   * BOTH FACTS ARE RE-READ AFTERWARDS, because two changed: which lists exist,
   * and which one is current. The server re-points `current_list_id` at the
   * primary when it deletes the current list -- without asking /api/me for the
   * answer, this screen would keep reading a list that is gone, and the roster
   * fetch would 404.
   */
  async function deleteList() {
    if (!listID) return;
    setListBusy(true);
    setListError(null);
    try {
      await api.deleteList(listID);
      const me = await api.me();
      await refresh();
      setConfirmingDelete(false);
      if (me.user) onUserChanged(me.user);
    } catch (err) {
      // The primary's refusal is stated in the client's own words, which are
      // the server's sentence said once: the UI refuses this too, and a reader
      // must not meet two different explanations of one rule.
      setListError(
        isConflict(err) ? t("manage.lists.deletePrimary") : t("error.couldNotSave"),
      );
    } finally {
      setListBusy(false);
    }
  }

  if (failed) return <p className={styles.error}>{t("error.couldNotLoad")}</p>;
  if (!listID || members === null || cap === null) return null;

  const toAdd = adding(chosen, members);
  const after = members.length + toAdd.length;
  const atCap = members.length >= cap;
  const memberIds = new Set(members.map((m) => m.fencer_id));

  // Fencer → the names of every list holding them, the open list first and the
  // rest in the account's own list order. Display only: the commit and the cap
  // still look at `memberIds`, the open list alone.
  const onLists = new Map<string, string[]>();
  const note = (fencerID: string, name: string) =>
    onLists.set(fencerID, [...(onLists.get(fencerID) ?? []), name]);
  if (openList) for (const id of memberIds) note(id, openList.name);
  for (const l of lists) {
    if (l.id === listID) continue;
    for (const id of otherRosters.get(l.id) ?? []) note(id, l.name);
  }

  // The two steps replace the body and keep the heading, so the screen never
  // loses its own name or its way back to Settings.
  if (granted && step === "create") {
    return (
      <ManageFencersFrame>
        {listError ? (
          <p className={styles.error} role="alert">
            {listError}
          </p>
        ) : null}
        <CreateListStep
          busy={listBusy}
          onCancel={() => {
            setListError(null);
            setStep("roster");
          }}
          onCreate={(name, description) => void createList(name, description)}
        />
      </ManageFencersFrame>
    );
  }

  if (granted && step === "notifications" && created) {
    return (
      <ManageFencersFrame>
        <NewListNotificationsStep
          list={created}
          primaryName={primary && primary.id !== created.id ? primary.name : null}
          onDone={() => {
            setCreated(null);
            setStep("roster");
          }}
        />
      </ManageFencersFrame>
    );
  }

  return (
    <ManageFencersFrame>
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      {listError ? (
        <p className={styles.error} role="alert">
          {listError}
        </p>
      ) : null}

      {/*
        THE LIST SECTION, and the whole of it is behind the flag: for an
        ungranted account this screen renders exactly what it rendered before
        multi-list existed, which is this item's regression property.
      */}
      {granted && openList ? (
        <div className={styles.lists}>
          {/*
            THE PICKER RENDERS ONLY ABOVE ONE LIST (plural chrome for plural
            data, and rule 1's second condition). Single-select, because this
            names the ONE list being edited -- the multi-select control the
            views take is a different question and arrives with build item 3.
          */}
          {lists.length > 1 ? (
            <label className={styles.listsLabel}>
              {t("manage.lists.picker")}{" "}
              <select
                className={styles.select}
                value={openList.id}
                disabled={listBusy}
                onChange={(e) => void switchList(e.target.value)}
              >
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className={styles.listName}>{openList.name}</span>
          )}

          {/* In WORDS, never by colour alone: it is the one list Delete refuses. */}
          {openList.is_primary ? (
            <span className={styles.primaryMark}>{t("manage.lists.primaryMark")}</span>
          ) : null}

          {renaming ? (
            <RenameListForm
              name={openList.name}
              busy={listBusy}
              onCancel={() => {
                setListError(null);
                setRenaming(false);
              }}
              onSave={(name) => void renameList(name)}
            />
          ) : confirmingDelete ? (
            /*
              IN PLACE RATHER THAN IN A MODAL (decision 481 on 370's posture: a
              destructive confirmation inside a transient overlay invites
              dismissal mid-decision). A confirmation and not an undo, because
              unlike removing a fencer this cannot be undone -- the roster, the
              notification settings and the list's place in every view scope go
              with it.
            */
            <span className={styles.confirm}>
              {t("manage.lists.confirmDelete", { name: openList.name })}
              <button
                className={styles.listVerbDanger}
                type="button"
                disabled={listBusy}
                onClick={() => void deleteList()}
              >
                {t("manage.lists.confirmDeleteYes")}
              </button>
              <button
                className={styles.listVerb}
                type="button"
                disabled={listBusy}
                onClick={() => setConfirmingDelete(false)}
              >
                {t("manage.lists.cancel")}
              </button>
            </span>
          ) : (
            <span className={styles.listVerbs}>
              {/*
                ABSENT AT THE LIST CAP rather than refused after the press
                (Kevin): a control whose only outcome is a refusal is the one
                decision 418 rules out. The 409's cap sentence below stays for
                a cap lowered, or a list made elsewhere, since this read.
              */}
              {listCountCap !== undefined && lists.length >= listCountCap ? null : (
                <button
                  className={styles.listVerb}
                  type="button"
                  disabled={listBusy}
                  onClick={() => {
                    setListError(null);
                    setStep("create");
                  }}
                >
                  {t("manage.lists.new")}
                </button>
              )}
              <button
                className={styles.listVerb}
                type="button"
                disabled={listBusy}
                aria-label={t("manage.lists.renameNamed", { name: openList.name })}
                onClick={() => {
                  setListError(null);
                  setRenaming(true);
                }}
              >
                {t("manage.lists.rename")}
              </button>
              {/*
                ABSENT ON THE PRIMARY rather than disabled: "make this the
                primary" on the list that already is has no outcome, and a
                control with no outcome is one decision 418 rules out. The
                word beside the name is what says so instead.
              */}
              {openList.is_primary ? null : (
                <button
                  className={styles.listVerb}
                  type="button"
                  disabled={listBusy}
                  aria-label={t("manage.lists.makePrimaryNamed", { name: openList.name })}
                  onClick={() => void makePrimary()}
                >
                  {t("manage.lists.makePrimary")}
                </button>
              )}
              {/*
                REFUSED IN THE UI FOR THE PRIMARY, with the sentence the server
                answers its 409 with -- said once, so a reader cannot meet two
                explanations of one rule.
                AND ONLY WHERE THE REMEDY EXISTS: with one list there is no
                other list to designate, so the sentence would be an
                instruction nobody can follow, standing where a control is not.
                The word "Primary list" beside the name is what says why there
                is no Delete.
              */}
              {openList.is_primary ? (
                lists.length > 1 ? (
                  <span className={styles.primaryMark}>{t("manage.lists.deletePrimary")}</span>
                ) : null
              ) : (
                <button
                  className={styles.listVerbDanger}
                  type="button"
                  disabled={listBusy}
                  aria-label={t("manage.lists.deleteNamed", { name: openList.name })}
                  onClick={() => {
                    setListError(null);
                    setConfirmingDelete(true);
                  }}
                >
                  {t("manage.lists.delete")}
                </button>
              )}
            </span>
          )}
        </div>
      ) : null}

      {/*
        TWO PANES, one job each: what is tracked, and how to add. §12's reflow rule
        names this screen by name — "manage-fencers roster/add side by side" — so
        the split is a media query over the same two sections, never a second
        layout.
      */}
      <div className={styles.panes}>
        <section className={styles.pane}>
          <div className={styles.paneHead}>
            <h2 className={styles.h2}>{t("manage.roster")}</h2>
            {/* §2's "N of 10", in the neutral phrasing used everywhere else. */}
            <span className={`tc-num ${styles.count}`}>
              {t("search.countOf", { count: members.length, cap })}
            </span>
          </div>

          {members.length === 0 ? (
            <p className={styles.empty}>{t("manage.empty")}</p>
          ) : (
            <ul className={styles.roster}>
              {members.map((m) => (
                <li key={m.fencer_id} className={styles.member}>
                  <div className={styles.memberMain}>
                    <span className={styles.memberName}>{m.name}</span>
                    {/* Club only when there is one. No "no club" line. */}
                    {m.club ? <span className={styles.memberMeta}>{m.club}</span> : null}
                  </div>
                  {/*
                    Red as an outline, not filled: a roster of ten filled-red
                    Remove buttons would be ten primary actions on a screen whose
                    primary action is Add (principle 3).
                  */}
                  <button
                    className={styles.remove}
                    type="button"
                    /*
                      aria-label RATHER THAN a visually-hidden span, and this is a
                      real bug rather than a style preference. `Remove<span
                      class="sr-only"> NAME</span>` computes as "RemoveRofino,
                      Samuel" — the accessible-name algorithm trims each node's
                      text before joining, so the leading space is lost and a
                      screen reader announces one mangled word. Measured, not
                      assumed: `computeAccessibleName` returns exactly that.

                      The visible word stays inside the name, which is what WCAG
                      2.5.3 (Label in Name) requires for anyone driving the page
                      by voice.
                    */
                    aria-label={t("manage.removeNamed", { name: m.name })}
                    onClick={() => void remove(m)}
                  >
                    {t("manage.remove")}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/*
            THE UNDO. role="status" so it is announced without stealing focus — a
            removal the user just asked for is not an alert, and moving focus would
            interrupt someone removing two people in a row.
          */}
          {removed ? (
            <p className={styles.undo} role="status">
              {t("manage.removed", { name: removed.name })}{" "}
              <button className={styles.undoButton} type="button" onClick={() => void undoRemove()}>
                {t("manage.undo")}
              </button>
            </p>
          ) : null}
        </section>

        <section className={styles.pane}>
          <h2 className={styles.h2}>{t("manage.add")}</h2>

          {/*
            AT CAP, THE SEARCH IS STILL SHOWN and the commit is blocked. Hiding it
            would leave a user at ten with no way to see who else exists, and the
            honest sentence about the limit is more useful than an absent control.
            The cap passed down is the REMAINING room, so the search's own count and
            its at-cap message describe this batch rather than the whole roster.
          */}
          {/*
            THE COMMIT BAR TRAVELS WITH THE SELECTION (decision 326), passed as a
            slot so it renders beneath the chosen chips instead of below the whole
            results list. A search for a common surname returns enough rows to push
            a trailing button off a phone screen entirely, which turned "pick two
            and add them" into a scroll past everything already rejected.

            It still renders only once something is picked — the slot is inside the
            chips block, which has that condition already — so a permanently
            visible disabled Add button is still not chrome anyone has to look at.

            The arithmetic stays HERE rather than moving into FencerSearch: it
            needs `members.length`, the roster count, which the search component
            has no business knowing.
          */}
          <FencerSearch
            chosen={chosen}
            onChange={setChosen}
            cap={Math.max(cap - members.length, 0)}
            memberIds={memberIds}
            onLists={onLists}
            commit={
              <div className={styles.commitBar}>
                <p className={`tc-num ${styles.math}`}>
                  {t("manage.selected", { count: chosen.length })} ·{" "}
                  {t("manage.afterAdding", { count: after, cap })}
                </p>
                <button
                  className={styles.primary}
                  type="button"
                  onClick={() => void commitAdds()}
                  // AT-CAP BLOCKED COMMIT, which the inventory names as its own
                  // state. Blocked rather than trimmed: adding the first two of
                  // three picks and silently dropping the third is the failure
                  // nobody notices until a tournament.
                  disabled={busy || after > cap}
                >
                  {t("manage.addCount", { count: toAdd.length })}
                </button>
              </div>
            }
          />

          {atCap ? <p className={styles.atCap}>{t("manage.atCap", { cap })}</p> : null}
        </section>
      </div>
    </ManageFencersFrame>
  );
}

/**
 * The screen's heading and its way out, around whatever body is current.
 *
 * ONE FRAME FOR ALL THREE BODIES, so a step cannot lose the screen's name or
 * its link back to Settings — which a reader mid-create needs most, being the
 * moment they are most likely to have arrived somewhere they did not mean to.
 */
function ManageFencersFrame({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className={styles.screen}>
      <div className={styles.head}>
        <h1 className={styles.h1}>{t("settings.fencers")}</h1>
        {/*
          Back to Settings as a real anchor, because §2 puts the entry to this
          screen there. Not history.back(): the screen is also reachable by URL, and
          a bookmark should still have somewhere sensible to go.
        */}
        <Link className={styles.back} to="/settings">
          {t("manage.backToSettings")}
        </Link>
      </div>
      {children}
    </div>
  );
}

/**
 * Renaming, in place beside the name it is changing.
 *
 * AT MODULE SCOPE like every other component in this tree (decision 492): one
 * declared inside the screen's render would be a new component type on every
 * keystroke, so the input would be remounted and focus would leave the field
 * after the first character.
 */
function RenameListForm({
  name,
  busy,
  onSave,
  onCancel,
}: {
  name: string;
  busy: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(name);

  return (
    <form
      className={styles.confirm}
      onSubmit={(e) => {
        e.preventDefault();
        onSave(value);
      }}
    >
      <input
        className={styles.input}
        value={value}
        maxLength={100}
        aria-label={t("manage.lists.nameLabel")}
        onChange={(e) => setValue(e.target.value)}
      />
      <button className={styles.listVerb} type="submit" disabled={busy || !value.trim()}>
        {t("manage.lists.save")}
      </button>
      <button className={styles.listVerb} type="button" disabled={busy} onClick={onCancel}>
        {t("manage.lists.cancel")}
      </button>
    </form>
  );
}

/**
 * Step one of the create flow: what is it called?
 *
 * A NAME AND AN OPTIONAL DESCRIPTION, which is what the table holds and what
 * the rename verb can change afterwards. Nothing about notifications is asked
 * here — that is the next step, and asking two questions on one screen would
 * make the second one look optional.
 */
function CreateListStep({
  busy,
  onCreate,
  onCancel,
}: {
  busy: boolean;
  onCreate: (name: string, description: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  return (
    <form
      className={styles.step}
      onSubmit={(e) => {
        e.preventDefault();
        onCreate(name, description);
      }}
    >
      <h2 className={styles.stepHeading}>{t("manage.lists.createHeading")}</h2>
      <p className={styles.stepDetail}>{t("manage.lists.createDetail")}</p>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t("manage.lists.nameLabel")}</span>
        <input
          className={styles.input}
          value={name}
          maxLength={100}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t("manage.lists.descriptionLabel")}</span>
        <input
          className={styles.input}
          value={description}
          maxLength={280}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className={styles.stepActions}>
        {/* The filled red is the commit, and it is the only one on this body. */}
        <button className={styles.primary} type="submit" disabled={busy || !name.trim()}>
          {t("manage.lists.create")}
        </button>
        <button className={styles.listVerb} type="button" disabled={busy} onClick={onCancel}>
          {t("manage.lists.cancel")}
        </button>
      </div>
    </form>
  );
}

/**
 * Step two: what should the new list send? (plan rule 6, the item's D5.)
 *
 * ASKED AT THE MOMENT THE LIST IS SAVED, because that is when somebody knows
 * why they made it. A redirect to the settings page instead would make this a
 * chore to be found later, and the list would sit silent in the meantime
 * without anybody having been told it was.
 *
 * IT STARTS FROM THE SILENCE ROWS, which is the true state rather than a blank
 * form: the create transaction wrote "everything off" and the tree renders
 * exactly that, so the reader is turning things ON from a known floor.
 *
 * THE COPY ACTION IS AN ENDPOINT, not a replay of the ladder from this screen:
 * copying the stored overrides keeps the two lists agreeing about kinds that do
 * not exist yet, where replaying resolved values would freeze today's defaults
 * into the new list.
 *
 * THE WAY OUT IS "SKIP" UNTIL SOMETHING IS CHANGED AND "DONE" AFTERWARDS. Every
 * change here is already saved when it is made (the settings page's rule), so
 * there is nothing to discard — but a control labelled Skip beside a ladder the
 * reader has just been setting would misdescribe what pressing it does.
 */
function NewListNotificationsStep({
  list: target,
  primaryName,
  onDone,
}: {
  list: ListRow;
  primaryName: string | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<AlertPreferences | null>(null);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .alertPreferences(target.id)
      .then((payload) => {
        if (!cancelled) setPrefs(payload);
      })
      .catch(() => {
        if (!cancelled) setError(t("error.couldNotLoad"));
      });
    return () => {
      cancelled = true;
    };
  }, [target.id, t]);

  const set: SetPreference = (scope, selector, channel, enabled) => {
    setError(null);
    api
      .updateAlertPreferences(target.id, [{ scope, selector, channel, enabled }])
      .then((payload) => {
        setPrefs(payload);
        setTouched(true);
      })
      .catch(() => setError(t("notifications.manage.saveFailed")));
  };

  function copyFromPrimary() {
    setError(null);
    api
      .copyAlertPreferencesFromPrimary(target.id)
      .then((payload) => {
        setPrefs(payload);
        setTouched(true);
      })
      .catch(() => setError(t("notifications.manage.saveFailed")));
  }

  return (
    <div className={styles.step}>
      <h2 className={styles.stepHeading}>
        {t("manage.lists.notificationsHeading", { name: target.name })}
      </h2>
      <p className={styles.stepDetail}>{t("manage.lists.notificationsDetail")}</p>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <div className={styles.stepActions}>
        {/*
          Offered only where there is a primary to copy FROM that is not this
          list -- which is every case today, since a created list is never the
          primary, but a control naming a list must not be able to name this
          one.
        */}
        {primaryName ? (
          <button className={styles.listVerb} type="button" onClick={copyFromPrimary}>
            {t("manage.lists.copyFromPrimary", { name: primaryName })}
          </button>
        ) : null}
        <button className={styles.listVerb} type="button" onClick={onDone}>
          {touched ? t("manage.lists.done") : t("manage.lists.skip")}
        </button>
      </div>

      {prefs === null ? null : <PreferenceTree prefs={prefs} onSet={set} />}
    </div>
  );
}
